// Trust & legal content for Tracksy. Plain-language, product-accurate policies.
// NOTE: Confirm the placeholders marked [CONFIRM] with your real business details
// (legal entity name, registered address, ABN/company number) before launch.

export const CONTACT_EMAIL = "tracksysupport@gmail.com";
export const BUSINESS_NAME = "Tracksy"; // [CONFIRM] registered legal entity name
export const BUSINESS_LOCATION = "Australia"; // [CONFIRM] registered business location
export const LAST_UPDATED = "July 2026";

const p = (t) => t;
const list = (items) => ({ list: items });

export const LEGAL = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    seo: "How Tracksy collects, uses and protects your personal information.",
    intro:
      "This Privacy Policy explains what information Tracksy collects when you visit our website or place an order, how we use it, and the choices you have.",
    sections: [
      {
        h: "Information we collect",
        body: [
          p("We collect the information you provide and a limited amount of technical data, including:"),
          list([
            "Contact details — name, email address and phone number.",
            "Order details — products purchased, order value and order history.",
            "Shipping and billing address, so we can deliver your order.",
            "Payment information — processed securely by Stripe. We never see or store your full card number.",
            "Technical data — IP address, browser type and basic usage analytics to keep the site secure and working.",
          ]),
        ],
      },
      {
        h: "How we use your information",
        body: [
          list([
            "To process, fulfil and ship your orders and send order confirmations.",
            "To provide customer support and respond to enquiries.",
            "To send product updates or offers if you have opted in (you can unsubscribe at any time).",
            "To detect, prevent and address fraud, abuse or security issues.",
            "To comply with our legal and tax obligations.",
          ]),
        ],
      },
      {
        h: "Payments",
        body: [
          p(
            "Payments are processed by Stripe, a PCI-DSS compliant payment provider. Your card details are entered on Stripe's secure checkout and are never transmitted to or stored on our servers. Stripe's handling of your data is governed by Stripe's own privacy policy.",
          ),
        ],
      },
      {
        h: "Sharing your information",
        body: [
          p("We do not sell your personal information. We share it only with trusted parties that help us run the store:"),
          list([
            "Payment processor (Stripe) to take payment.",
            "Fulfilment and shipping partners to deliver your order.",
            "Service providers who help us operate the website and email.",
            "Authorities where required by law.",
          ]),
        ],
      },
      {
        h: "Cookies",
        body: [
          p(
            "We use essential cookies to make the site and cart work, and basic analytics cookies to understand how the site is used. You can control cookies through your browser settings.",
          ),
        ],
      },
      {
        h: "Data retention",
        body: [
          p("We keep order and contact records for as long as needed to fulfil orders, provide support and meet legal and tax requirements, then delete or anonymise them."),
        ],
      },
      {
        h: "Your rights",
        body: [
          p("You can ask us to access, correct or delete your personal information, or to stop sending you marketing email. To make a request, contact us at the email below and we will respond within a reasonable time."),
        ],
      },
      {
        h: "Changes to this policy",
        body: [p("We may update this policy from time to time. The latest version will always be posted on this page with its effective date.")],
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    seo: "The terms that apply when you use the Tracksy website and buy our products.",
    intro:
      "These Terms of Service govern your use of the Tracksy website and your purchase of our products. By using the site or placing an order, you agree to these terms.",
    sections: [
      {
        h: "About our products",
        body: [
          p(
            "Tracksy is a compact Bluetooth tracker that works with the Apple Find My network. It is not a cellular or real-time GPS tracker and does not use SIM cards or a subscription. Location updates depend on a compatible Apple device passing within Bluetooth range of the tracker.",
          ),
          p(
            "Apple, Find My, iPhone and iPad are trademarks of Apple Inc. Tracksy is an independent brand and is not affiliated with, authorised or endorsed by Apple Inc.",
          ),
        ],
      },
      {
        h: "Orders and pricing",
        body: [
          list([
            "All prices are shown in Australian Dollars (AUD) unless stated otherwise.",
            "Taxes and shipping, where applicable, are calculated and shown at checkout.",
            "We reserve the right to correct pricing errors and to refuse or cancel any order, in which case you will receive a full refund.",
            "An order is confirmed only once payment is successfully processed.",
          ]),
        ],
      },
      {
        h: "Shipping and delivery",
        body: [p("Delivery times and costs are described in our Shipping Policy. Delivery estimates are provided in good faith but are not guaranteed, as some factors are outside our control.")],
      },
      {
        h: "Returns and refunds",
        body: [p("Your return and refund rights are described in our Refund & Returns Policy. Nothing in these terms limits any rights you have under applicable consumer law.")],
      },
      {
        h: "Warranty and liability",
        body: [
          p(
            "Tracksy products carry a 12-month warranty against manufacturing defects. To the fullest extent permitted by law, we are not liable for indirect or consequential loss, or for loss arising from misuse, or from reliance on the tracker as a sole security, recovery or safety device.",
          ),
        ],
      },
      {
        h: "Acceptable use",
        body: [
          p("You agree to use Tracksy products lawfully and only to track property you own or have permission to track. You must not use the product to unlawfully track or monitor another person."),
        ],
      },
      {
        h: "Intellectual property",
        body: [p("All content on this website, including the Tracksy name, logo and imagery, is owned by us or our licensors and may not be used without permission.")],
      },
      {
        h: "Governing law",
        body: [p("These terms are governed by the laws of the business's place of registration. Any disputes will be handled by the courts of that jurisdiction.")],
      },
      {
        h: "Changes",
        body: [p("We may update these terms from time to time. The current version will always be posted on this page.")],
      },
    ],
  },

  refund: {
    slug: "refund",
    title: "Refund & Returns Policy",
    seo: "Tracksy's 30-day returns, refunds and warranty terms in plain language.",
    intro:
      "We want you to be happy with your Tracksy. If something isn't right, here's how returns, refunds and warranty claims work.",
    sections: [
      {
        h: "30-day change-of-mind returns",
        body: [
          p(
            "You can return unopened, unused packs within 30 days of delivery for a refund of the product price. Items must be in their original, resalable condition and packaging.",
          ),
        ],
      },
      {
        h: "Faulty or damaged items",
        body: [
          p(
            "If your tracker arrives faulty, damaged or not as described, contact us within 30 days and we'll arrange a replacement or full refund, including any return postage. This is in addition to your rights under applicable consumer law.",
          ),
        ],
      },
      {
        h: "12-month warranty",
        body: [p("All Tracksy trackers are covered by a 12-month warranty against manufacturing defects. If a defect appears within that period, contact us for a replacement.")],
      },
      {
        h: "How to start a return",
        body: [
          p("Email us with your order number and the reason for the return. We'll confirm the return address and next steps."),
          list([
            "Include your order number and the email used to order.",
            "Tell us whether you'd like a refund or a replacement.",
            "For faulty items, a photo or short description helps us resolve it faster.",
          ]),
        ],
      },
      {
        h: "Refund timing",
        body: [
          p(
            "Once we receive and inspect your return (or approve a faulty-item claim), we'll process your refund to your original payment method, usually within 5–10 business days. Your bank may take a little longer to show it.",
          ),
        ],
      },
      {
        h: "Non-returnable items",
        body: [p("For hygiene and safety, opened or used items cannot be returned for change of mind, but are still covered by the faulty-item and warranty terms above.")],
      },
    ],
  },

  shipping: {
    slug: "shipping",
    title: "Shipping Policy",
    seo: "How and when Tracksy orders are processed, shipped and delivered.",
    intro: "Here's what to expect after you place an order with Tracksy.",
    sections: [
      {
        h: "Order processing",
        body: [p("Orders are processed within 1–3 business days. You'll receive an email confirmation when your order is placed and again when it ships.")],
      },
      {
        h: "Delivery estimates",
        body: [
          p(
            "Estimated delivery is typically 7–15 business days, depending on your location. Some orders are fulfilled from overseas partners, so remote destinations may take longer. Delivery estimates are not guaranteed.",
          ),
        ],
      },
      {
        h: "Shipping cost",
        body: [p("Shipping is tracked on every order. Shipping is free on orders over $50; a flat shipping fee applies below that and is shown at checkout.")],
      },
      {
        h: "Tracking",
        body: [p("Once your order ships, we'll email you a tracking number so you can follow it to your door.")],
      },
      {
        h: "Customs and duties",
        body: [p("For international orders, any import duties or taxes charged by your country are the responsibility of the recipient.")],
      },
      {
        h: "Lost or delayed orders",
        body: [p("If your order is significantly delayed or appears lost, contact us and we'll help track it down or arrange a replacement.")],
      },
    ],
  },
};

export const LEGAL_LINKS = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/refund", label: "Refund & Returns" },
  { to: "/shipping", label: "Shipping Policy" },
  { to: "/contact", label: "Contact us" },
];
